import { supabase } from './supabaseClient';
import {
  BOX_ZUSCHLAG, GEPRUEFT_BONUS, GRAD_LAT_METER, KandidatenNachKategorie, KATEGORIEN, LatLng,
  NotfallKategorie, OsmTyp, StellenKandidat, SUCHRADIUS, entfernungMeter, leereKandidaten,
  sortiereKandidaten, stellenSchluessel,
} from './notfallStellen';

/**
 * Die geprüfte Notfallstellen-Datenbank (`public.notfall_stellen`,
 * MIGRATIONS.md Nr. 34) — die einzige Stelle, die mit ihr redet.
 *
 * ─── Wozu sie da ist ───────────────────────────────────────────────────────
 * Jede Umkreissuche fragte bisher OpenStreetMap, jedes Mal aufs Neue, für
 * jedes Projekt. Dabei liegen die Baustellen eines Betriebs immer wieder in
 * denselben Landkreisen, und das Krankenhaus wechselt nicht. Was ein
 * Mitarbeiter einmal bestätigt hat, wird hier mit Koordinaten abgelegt und ist
 * beim nächsten Mal ohne Netzzugriff da — richtig UND sofort.
 *
 * Bestätigt oder verworfen: `status` kennt beides. Ein verworfener Eintrag
 * ist genauso wertvoll wie ein bestätigter, denn er verhindert dauerhaft, dass
 * derselbe Fehlgriff (die Kinderklinik, die zufällig am nächsten liegt) erneut
 * vorgeschlagen wird — auch dann, wenn kein Textmuster ihn erkennt.
 *
 * ─── Warum hier nichts nach oben wirft ─────────────────────────────────────
 * Die Tabelle ist eine Beschleunigung, keine Voraussetzung. Fehlt die
 * Migration (auf PROD ist Nr. 34 zunächst offen — PostgREST antwortet dann mit
 * `42P01`), ist der Nutzer offline oder greift RLS, dann gibt es eben keine
 * geprüften Stellen: die Umkreissuche läuft wie zuvor. Fehler landen als
 * `console.warn` im Protokoll, nicht in der Oberfläche. Einzige Ausnahme ist
 * `merkeStelle` — dort hat der Nutzer ausdrücklich auf „Bestätigen" gedrückt
 * und muss erfahren, wenn das Merken nicht geklappt hat.
 */

export type StellenStatus = 'bestaetigt' | 'verworfen';

export interface GepruefteStelle {
  id: string;
  schluessel: string;
  kategorie: NotfallKategorie;
  status: StellenStatus;
  name: string;
  adresse: string;
  telefon: string;
  lat: number;
  lng: number;
  osmTyp: OsmTyp | null;
  osmId: number | null;
  hinweis: string | null;
  geprueftVonName: string | null;
  geprueftAm: string | null;
}

export interface GeprueftesUmfeld {
  /** Bestätigte Stellen als fertige Kandidaten, je Kategorie nach Rang. */
  kandidaten: KandidatenNachKategorie;
  /** Schlüssel verworfener Stellen — fliegen aus jeder OSM-Trefferliste. */
  gesperrt: Set<string>;
  /** false, wenn die Datenbank nicht antworten konnte (Migration/Offline). */
  erreichbar: boolean;
}

const TABELLE = 'notfall_stellen';

const zeileZuStelle = (row: any): GepruefteStelle | null => {
  if (!row || typeof row.lat !== 'number' || typeof row.lng !== 'number') return null;
  if (!KATEGORIEN.includes(row.kategorie)) return null;
  return {
    id: row.id,
    schluessel: row.schluessel,
    kategorie: row.kategorie,
    status: row.status === 'verworfen' ? 'verworfen' : 'bestaetigt',
    name: row.name || '',
    adresse: row.adresse || '',
    telefon: row.telefon || '',
    lat: row.lat,
    lng: row.lng,
    osmTyp: row.osm_typ || null,
    osmId: row.osm_id != null ? Number(row.osm_id) : null,
    hinweis: row.hinweis || null,
    geprueftVonName: row.geprueft_von_name || null,
    geprueftAm: row.geprueft_am || null,
  };
};

/**
 * Eine bestätigte Stelle kommt als `geeignet` an.
 *
 * Ein Mensch hat sie angesehen und für richtig befunden; dieses Urteil steht
 * über der Textmuster-Bewertung aus notfallStellen.ts. Der gespeicherte
 * `hinweis` bleibt sichtbar, damit man sieht, WAS bestätigt wurde.
 *
 * Der Rangvorteil steckt in `GEPRUEFT_BONUS` und ist bewusst nur ein Vorsprung
 * von 20 %: „geprüft" heißt „die Angaben stimmen", nicht „das ist überall die
 * nächste Stelle". Eine bestätigte Apotheke im Nachbarort darf die 500 m
 * entfernte nicht verdrängen.
 */
const stelleZuKandidat = (s: GepruefteStelle, center: LatLng): StellenKandidat => {
  const entfernung = entfernungMeter(center, s);
  return {
    kategorie: s.kategorie,
    name: s.name,
    adresse: s.adresse,
    telefon: s.telefon,
    lat: s.lat,
    lng: s.lng,
    entfernung: Math.round(entfernung),
    quelle: 'db',
    osmTyp: s.osmTyp || undefined,
    osmId: s.osmId ?? undefined,
    geprueftVon: s.geprueftVonName || undefined,
    geprueftAm: s.geprueftAm || undefined,
    eignung: 'geeignet',
    hinweis: s.hinweis || undefined,
    rang: entfernung * GEPRUEFT_BONUS,
  };
};

/**
 * Geprüfte Stellen im Umkreis — EINE Abfrage über ein Koordinatenfenster.
 *
 * Bewusst ohne RPC: ein Rechteck aus `lat`/`lng`-Vergleichen beantwortet
 * PostgREST direkt über den Index, und es gibt keine Funktion, die auf einer
 * Umgebung ohne Migration mit `PGRST202` fehlschlagen könnte. Die echte
 * Luftlinie wird anschließend hier gerechnet — das Rechteck ist nur der
 * Grobfilter und schneidet an den Ecken großzügig zu viel ein.
 */
export const ladeGeprueftesUmfeld = async (center: LatLng, radiusM: number): Promise<GeprueftesUmfeld> => {
  const kandidaten = leereKandidaten();
  const gesperrt = new Set<string>();
  try {
    // Dasselbe Fenster wie die Overpass-Abfrage, inklusive Sicherheitszuschlag:
    // ein geprüfter Eintrag darf nicht an der Rundung des Grobfilters scheitern.
    const dLat = (radiusM * BOX_ZUSCHLAG) / GRAD_LAT_METER;
    const cos = Math.max(0.01, Math.cos(center.lat * Math.PI / 180));
    const dLng = (radiusM * BOX_ZUSCHLAG) / (GRAD_LAT_METER * cos);

    const { data, error } = await supabase
      .from(TABELLE)
      .select('id, schluessel, kategorie, status, name, adresse, telefon, lat, lng, osm_typ, osm_id, hinweis, geprueft_von_name, geprueft_am')
      .gte('lat', center.lat - dLat).lte('lat', center.lat + dLat)
      .gte('lng', center.lng - dLng).lte('lng', center.lng + dLng)
      .limit(500);
    if (error) throw error;

    for (const row of data || []) {
      const stelle = zeileZuStelle(row);
      if (!stelle) continue;
      if (stelle.status === 'verworfen') {
        gesperrt.add(stelle.schluessel || stellenSchluessel(stelle.kategorie, stelle));
        continue;
      }
      // Je Kategorie ihr eigener Radius — sonst stünde die bestätigte Apotheke
      // aus 20 km Entfernung in einer Liste, in der die OSM-Suche nur 6 km weit
      // schaut, und täuschte eine Nähe vor, die es nicht gibt.
      const entfernt = entfernungMeter(center, stelle);
      if (entfernt > Math.min(radiusM, SUCHRADIUS[stelle.kategorie])) continue;
      kandidaten[stelle.kategorie].push(stelleZuKandidat(stelle, center));
    }
    for (const k of KATEGORIEN) kandidaten[k] = sortiereKandidaten(kandidaten[k]);
    return { kandidaten, gesperrt, erreichbar: true };
  } catch (e) {
    console.warn('Geprüfte Notfallstellen nicht abrufbar — es wird nur OpenStreetMap befragt.', e);
    return { kandidaten, gesperrt, erreichbar: false };
  }
};

/** Anzeigename des Prüfers, einmal je Sitzung geholt. */
let prueferName: string | null | undefined;

const ermittlePrueferName = async (): Promise<string | null> => {
  if (prueferName !== undefined) return prueferName;
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { prueferName = null; return prueferName; }
    const { data: profil } = await supabase
      .from('profiles').select('first_name, last_name').eq('id', user.id).single();
    const name = [profil?.first_name, profil?.last_name].filter(Boolean).join(' ').trim();
    prueferName = name || user.email || null;
  } catch (e) {
    console.warn('Prüfername nicht ermittelbar', e);
    prueferName = null;
  }
  return prueferName;
};

export interface ZuMerkendeStelle {
  kategorie: NotfallKategorie;
  status: StellenStatus;
  name: string;
  adresse?: string;
  telefon?: string;
  lat: number;
  lng: number;
  osmTyp?: OsmTyp;
  osmId?: number;
  hinweis?: string;
}

/**
 * Bestätigung oder Ablehnung eines Mitarbeiters festhalten.
 *
 * `upsert` über `schluessel`: derselbe Ort wird nie doppelt abgelegt, eine
 * erneute Prüfung überschreibt Name, Telefon und Prüfvermerk. Der Schlüssel
 * kommt aus `stellenSchluessel` und ist deshalb eine gewöhnliche, eindeutige
 * Spalte — ein partieller Index taugte nicht als Arbiter (MIGRATIONS.md Nr. 32).
 *
 * Wirft bei Fehlern: hier hat jemand bewusst geklickt und muss erfahren, wenn
 * die Bestätigung nicht angekommen ist.
 */
export const merkeStelle = async (stelle: ZuMerkendeStelle): Promise<GepruefteStelle> => {
  const name = (stelle.name || '').trim();
  if (!name) throw new Error('Ohne Namen lässt sich die Stelle nicht merken.');
  if (!Number.isFinite(stelle.lat) || !Number.isFinite(stelle.lng)) {
    throw new Error('Ohne Koordinaten lässt sich die Stelle nicht merken — bitte zuerst automatisch ermitteln.');
  }

  const schluessel = stellenSchluessel(stelle.kategorie, stelle);
  const geprueftVonName = await ermittlePrueferName();
  const { data: { user } } = await supabase.auth.getUser().catch(() => ({ data: { user: null } } as any));

  const zeile = {
    schluessel,
    kategorie: stelle.kategorie,
    status: stelle.status,
    name,
    adresse: (stelle.adresse || '').trim() || null,
    telefon: (stelle.telefon || '').trim() || null,
    lat: stelle.lat,
    lng: stelle.lng,
    osm_typ: stelle.osmTyp || null,
    osm_id: stelle.osmId ?? null,
    hinweis: (stelle.hinweis || '').trim() || null,
    geprueft_von: user?.id || null,
    geprueft_von_name: geprueftVonName,
    geprueft_am: new Date().toISOString(),
  };

  const { data, error } = await supabase
    .from(TABELLE)
    .upsert(zeile, { onConflict: 'schluessel' })
    .select()
    .single();
  if (error) throw error;

  const gemerkt = zeileZuStelle(data);
  if (!gemerkt) throw new Error('Die gemerkte Stelle kam unvollständig zurück.');
  return gemerkt;
};
